
import { forwardRef, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Stage, Layer, Rect, Text, Group, Label, Tag } from "react-konva";
import {
    CHUNK_TYPE_COLOR,
    JOB_TYPE,
    WORD_BOX_COLOR,
} from "../../constants/constants";
import { sortChunksByNum } from "../../helpers/editorHelper";

const PageView = forwardRef(
    ({ page, jobType, zoom, hoveredChunkId, setHoveredChunkId }, ref) => {
        const isDeepExtract = jobType === JOB_TYPE.DEEP_EXTRACT;
        const baseWidth = page.width_px;
        const baseHeight = page.height_px;

        const width = baseWidth * zoom;
        const height = baseHeight * zoom;

        const [boxes, setBoxes] = useState(
            (isDeepExtract ? sortChunksByNum(page.chunks) : page.chunks).map((chunk) => ({
                id: chunk.external_id,
                type: chunk.chunk_type,
                x: chunk.bbox_left,
                y: chunk.bbox_top,
                width: chunk.bbox_right - chunk.bbox_left,
                height: chunk.bbox_bottom - chunk.bbox_top,
                num: chunk.num,
            }))
        );

        // Deep extract: every word of every text chunk, in page coordinates
        const words = useMemo(() => {
            if (!isDeepExtract) return [];
            return page.chunks.flatMap((chunk) =>
                (chunk.words || []).map((word) => ({
                    x: word.bbox_left,
                    y: word.bbox_top,
                    width: word.bbox_right - word.bbox_left,
                    height: word.bbox_bottom - word.bbox_top,
                }))
            );
        }, [isDeepExtract, page.chunks]);
        
        const setCursor = (e, cursor) => {
            e.target.getStage().container().style.cursor = cursor;
        };

        const [isDrawing, setIsDrawing] = useState(false);
        const [newBox, setNewBox] = useState(null);

        const getNormalizedPos = (stage, point) => ({
            x: point.x / width,
            y: point.y / height,
        });

        const handleMouseDown = (e) => {
            if (e.target !== e.target.getStage()) return;

            const stage = e.target.getStage();
            const point = stage.getPointerPosition();

            const pos = getNormalizedPos(stage, point);

            setNewBox({
                id: Date.now(),
                ...pos,
                width: 0,
                height: 0,
            });

            setIsDrawing(true);
        };

        const handleMouseMove = (e) => {
            if (!isDrawing || !newBox) return;

            const stage = e.target.getStage();
            const point = stage.getPointerPosition();

            const pos = getNormalizedPos(stage, point);

            setNewBox((prev) => ({
                ...prev,
                width: pos.x - prev.x,
                height: pos.y - prev.y,
            }));
        };

        const handleMouseUp = () => {
            if (newBox) {
                const normalized = {
                    ...newBox,
                    x:
                        newBox.width < 0
                            ? newBox.x + newBox.width
                            : newBox.x,
                    y:
                        newBox.height < 0
                            ? newBox.y + newBox.height
                            : newBox.y,
                    width: Math.abs(newBox.width),
                    height: Math.abs(newBox.height),
                };

                setBoxes((prev) => [...prev, normalized]);
            }

            setIsDrawing(false);
            setNewBox(null);
        };

        return (
            <div
                ref={ref}
                data-page={page.page_num}
                style={{
                    position: "relative",
                    width,
                    height,
                    background: "#fff",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                    flexShrink: 0,
                }}
            >
                <img
                    src={page.gcs_image_url}
                    alt={`Page ${page.page_num}`}
                    draggable={false}
                    style={{
                        width: "100%",
                        height: "100%",
                        userSelect: "none",
                    }}
                />

                <Stage
                    width={width}
                    height={height}
                    style={{
                        position: "absolute",
                        inset: 0,
                    }}
                    //onMouseDown={handleMouseDown}
                    //onMouseMove={handleMouseMove}
                    //onMouseUp={handleMouseUp}
                    onMouseEnter={(e) => setCursor(e, "crosshair")}
                    onMouseLeave={(e) => setCursor(e, "default")}
                >
                    {isDeepExtract && (
                        <Layer listening={false}>
                            {/* WORD BOXES */}
                            {words.map((word, index) => (
                                <Rect
                                    key={index}
                                    x={word.x * width}
                                    y={word.y * height}
                                    width={word.width * width}
                                    height={word.height * height}
                                    stroke={WORD_BOX_COLOR}
                                    strokeWidth={1}
                                    perfectDrawEnabled={false}
                                />
                            ))}
                        </Layer>
                    )}

                    <Layer>
                        {/* EXISTING BOXES */}
                        {isDeepExtract && boxes.map((box) => {
                            const isHovered =
                                hoveredChunkId === box.id;
                            const color =
                                CHUNK_TYPE_COLOR[box.type] || CHUNK_TYPE_COLOR.text;

                            const x = box.x * width;
                            const y = box.y * height;
                            const w = box.width * width;
                            const h = box.height * height;

                            return (
                                <Group key={box.id}>
                                    {/* BOX */}
                                    <Rect
                                        x={x}
                                        y={y}
                                        width={w}
                                        height={h}
                                        stroke={color}
                                        strokeWidth={isHovered ? 3 : 1.5}
                                        fill={
                                            isHovered
                                                ? "rgba(219,197,236,0.25)"
                                                : "transparent"
                                        }
                                        onMouseEnter={() => {
                                            setHoveredChunkId(box.id);
                                            document.body.style.cursor =
                                                "pointer";
                                        }}
                                        onMouseLeave={() => {
                                            setHoveredChunkId(null);
                                            document.body.style.cursor =
                                                "crosshair";
                                        }}
                                    />

                                    {/* TYPE + NUM TAG */}
                                    <Label
                                        x={x}
                                        y={y >= 16 ? y - 16 : y}
                                        listening={false}
                                    >
                                        <Tag fill={color} cornerRadius={2} />
                                        <Text
                                            text={`${box.num ?? ""} ${box.type || "unknown"}`.trim()}
                                            fontSize={11}
                                            fontStyle="bold"
                                            padding={2}
                                            fill="#fff"
                                        />
                                    </Label>
                                </Group>
                            );
                        })}

                        {!isDeepExtract && boxes.map((box) => {
                            const isHovered =
                                hoveredChunkId === box.id;

                            const x = box.x * width;
                            const y = box.y * height;
                            const w = box.width * width;
                            const h = box.height * height;

                            return (
                                <Group key={box.id}>
                                    {/* BOX */}
                                    <Rect
                                        x={x}
                                        y={y}
                                        width={w}
                                        height={h}
                                        stroke={
                                            isHovered
                                                ? "#deb9ef"
                                                : "#00A3FF"
                                        }
                                        strokeWidth={
                                            isHovered ? 3 : 2
                                        }
                                        fill={
                                            isHovered
                                                ? "rgba(219,197,236,0.2)"
                                                : "rgba(0,163,255,0.15)"
                                        }
                                        onMouseEnter={(e) => { setHoveredChunkId(box.id); setCursor(e, "pointer"); }}
                                        onMouseLeave={(e) => { setHoveredChunkId(null); setCursor(e, "crosshair"); }}
                                    />

                                    {/* TOOLTIP */}
                                    {isHovered && (
                                        <Group>
                                            <Rect
                                                x={x}
                                                y={y - 28}
                                                width={90}
                                                height={22}
                                                fill="#deb9ef"
                                                cornerRadius={4}
                                                shadowBlur={4}
                                            />
                                            <Text
                                                x={x + 6}
                                                y={y - 24}
                                                text={box.type || "unknown"}
                                                fontSize={12}
                                                fill="#000"
                                            />
                                        </Group>
                                    )}
                                </Group>
                            );
                        })}

                        {newBox && (
                            <Rect
                                x={
                                    (newBox.width < 0
                                        ? newBox.x +
                                        newBox.width
                                        : newBox.x) * width
                                }
                                y={
                                    (newBox.height < 0
                                        ? newBox.y +
                                        newBox.height
                                        : newBox.y) * height
                                }
                                width={
                                    Math.abs(newBox.width) *
                                    width
                                }
                                height={
                                    Math.abs(newBox.height) *
                                    height
                                }
                                stroke="#00A3FF"
                                dash={[6, 6]}
                            />
                        )}
                    </Layer>
                </Stage>
            </div>
        );
    }
);

PageView.displayName = "PageView";

export default PageView;

PageView.propTypes = {
    page: PropTypes.object,
    jobType: PropTypes.string,
    zoom: PropTypes.number,
    hoveredChunkId: PropTypes.string,
    setHoveredChunkId: PropTypes.func,
};